import React from 'react';
import { Box, Card, Container, CssBaseline, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { QuotationServiceSimple } from '../../types';
import { useAgencyBranding } from '../../branding';
import { getCityNameCleaned } from '../../helpers';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(8),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'left',
	},
	introCard: {
		border: `1px solid ${theme.palette.primary.main}22`,
		boxShadow: '0 10px 28px rgba(49, 25, 75, 0.10)',
		borderRadius: 10,
		background: 'linear-gradient(135deg, #ffffff 0%, rgba(49, 25, 75, 0.035) 100%)',
	},
	helloTitle: {
		color: theme.palette.primary.main,
		fontWeight: 700,
		marginBottom: theme.spacing(1),
	},
	sectionHeader: {
		marginBottom: theme.spacing(1),
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		borderRadius: 10,
		fontSize: '1rem',
		color: 'white',
		fontWeight: 700,
		boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
	},
	includeGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
		gap: theme.spacing(1.5),
	},
	includeItem: {
		display: 'grid',
		gridTemplateColumns: '34px 1fr',
		gap: theme.spacing(1.25),
		alignItems: 'flex-start',
		padding: theme.spacing(1.5),
		borderRadius: 10,
		backgroundColor: '#fff',
		border: '1px solid rgba(49, 25, 75, 0.11)',
		boxShadow: '0 4px 14px rgba(49, 25, 75, 0.055)',
	},
	itemIcon: {
		width: 34,
		height: 34,
		borderRadius: 9,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(237, 102, 2, 0.12)',
		color: theme.palette.secondary.main,
	},
	listCard: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.primary.main}12`,
		boxShadow: '0 8px 24px rgba(49, 25, 75, 0.08)',
		borderRadius: 10,
	},
}));

export interface DestinationPreview {
	services?: QuotationServiceSimple[];
	cityCode: string;
	cityName: string;
	nights: number;
}

interface Props {
	destinations: DestinationPreview[];
	manualServices: QuotationServiceSimple[];
	notIncludes: string[];
	description: string;
	hasAir: boolean;
	hasHotels: boolean;
	isExternal?: boolean;
}

export const QuotationIntroPreview = (props: Props): JSX.Element => {
	const classes = useStyles();
	const { destinations, manualServices, notIncludes, description, hasAir, isExternal } = props;
	const branding = useAgencyBranding(isExternal);
	//const [introCity, setIntroCity] = useState<string>('');
	// useEffect(() => {
	// 	if (destinations?.length) {
	// 		const cityName = destinations[0] && destinations[0]?.cityName.split('-')[1];
	// 		cityName && setIntroCity(cityName);
	// 	}
	// }, [destinations]);

	const getTotalNights = (destinationCode: string) =>
		destinations.filter((x) => x.cityCode === destinationCode).reduce((a, b) => a + b.nights, 0);
	const uniqueDestinationNights = destinations.filter(
		(option, index) => destinations.findIndex((x) => x.cityCode === option.cityCode) === index,
	);
	const destinationServices = destinations.flatMap((option) => option.services || []);

	return (
		<Container component="main" maxWidth="md">
			<CssBaseline />
			<div className={classes.paper}>
				<Card className={classes.introCard} sx={{ padding: '16px', marginBottom: '16px' }}>
					<Typography component="h1" variant="h6" className={classes.helloTitle}>
						{branding.introTitle}
					</Typography>

					<Typography component="p" variant="body1">
						{branding.introText}
					</Typography>
				</Card>

				<Typography
					component="h1"
					variant="h6"
					className={classes.sectionHeader}
					sx={{ background: branding.headerGradient }}
				>
					Incluye:
				</Typography>
				<Card className={classes.listCard}>
					<Box className={classes.includeGrid}>
						{hasAir && (
							<Box className={classes.includeItem}>
								<Box className={classes.itemIcon}>
									<FlightTakeoffIcon fontSize="small" />
								</Box>
								<Box>
									<Typography variant="body1" fontWeight="bold">
										Boleto aéreo
									</Typography>
									<Typography component="p" variant="body2">
										Según itinerario con tasas e impuestos incluidos.
									</Typography>
								</Box>
							</Box>
						)}
						{props.hasHotels &&
							uniqueDestinationNights.map((option, index) => (
								<Box className={classes.includeItem} key={`hotel-${index}`}>
									<Box className={classes.itemIcon}>
										<HotelIcon fontSize="small" />
									</Box>
									<Box>
										<Typography variant="body1" fontWeight="bold">
											{getTotalNights(option.cityCode)} noches en {getCityNameCleaned(option.cityName)}
										</Typography>
										<Typography component="p" variant="body2">
											Alojamiento según la opción seleccionada.
										</Typography>
									</Box>
								</Box>
							))}
						{/* Acá va SOLO el nombre del servicio: la descripción completa vive en
						    "Observaciones de servicios incluidos" (QuotationServicesObservationPreview),
						    que recibe exactamente los servicios que tienen descripción. Repetirla
						    acá duplicaba texto largo y estiraba la lista de "Incluye". */}
						{destinationServices.map((service, serviceIndex) => (
							<Box className={classes.includeItem} key={`service-preview-${serviceIndex}`}>
								<Box className={classes.itemIcon}>
									<CheckCircleOutlineIcon fontSize="small" />
								</Box>
								<Box>
									<Typography variant="body1" fontWeight="bold">
										{service.name}
									</Typography>
								</Box>
							</Box>
						))}
						{manualServices.map((manualService, index) => (
							<Box className={classes.includeItem} key={`manual-service-preview-${index}`}>
								<Box className={classes.itemIcon}>
									<CheckCircleOutlineIcon fontSize="small" />
								</Box>
								<Box>
									<Typography variant="body1" fontWeight="bold">
										{manualService.name}
									</Typography>
								</Box>
							</Box>
						))}
					</Box>
				</Card>

				{notIncludes.length > 0 && (
					<>
						<Typography
							component="h1"
							variant="h6"
							className={classes.sectionHeader}
							sx={{ background: branding.headerGradient }}
						>
							No incluye:
						</Typography>
						<Card className={classes.listCard}>
							<Grid container spacing={1}>
								{notIncludes.map((option, index) => (
									<Grid item xs={12} sm={6} key={`not-includes-preview-${index}`}>
										<Box display="flex" gap={1} alignItems="flex-start">
											<HighlightOffIcon color="disabled" fontSize="small" />
											<Typography component="p" variant="body2">
												{option}.
											</Typography>
										</Box>
									</Grid>
								))}
							</Grid>
						</Card>
					</>
				)}
				{description && description.length > 0 && (
					<>
						<Typography
							component="h1"
							variant="h6"
							className={classes.sectionHeader}
							sx={{ background: branding.headerGradient }}
						>
							Comentarios del Asesor:
						</Typography>
						<Card className={classes.listCard}>
							<Box display="flex" gap={1.5} alignItems="flex-start">
								<Box className={classes.itemIcon}>
									<ChatBubbleOutlineIcon fontSize="small" />
								</Box>
								{/* Mismo tamaño/tipografía que los ítems de "No incluye" (body2):
								    los dos bloques son letra chica de la propuesta y deben leerse
								    con el mismo peso. */}
								<Typography style={{ whiteSpace: 'pre-line' }} component={'span'} variant={'body2'}>
									{description}
								</Typography>
							</Box>
						</Card>
					</>
				)}
			</div>
		</Container>
	);
};
